<template>
  <div class="bank-reconciliation-modern">
    <!-- Header Section -->
    <div class="reconciliation-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="fas fa-file-invoice-dollar title-icon"></i>
            Conciliação Bancária
          </h1>
          <p class="page-subtitle">
            Gerencie e concilie suas transações bancárias
          </p>
        </div>

        <div class="actions-section">
          <input
            type="file"
            ref="fileInput"
            accept=".ofx"
            @change="handleFileUpload"
            style="display: none"
          />
          <v-btn class="btn-upload-modern" @click="$refs.fileInput.click()">
            <i class="fas fa-file-upload mr-2"></i>
            Importar Extrato OFX
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Container -->
    <div class="reconciliation-container">
      <div class="mb-2">
        <ErrorsMessages :errors="errors" />
      </div>

      <!-- Account Information -->
      <div v-if="bankData" class="account-section">
        <div class="info-cards-grid">
          <div class="info-card-modern bank-info">
            <div class="info-icon">
              <i class="fas fa-university"></i>
            </div>
            <div class="info-content">
              <span class="info-label">Banco</span>
              <span class="info-value">{{ bankData.bankId }}</span>
            </div>
          </div>

          <div class="info-card-modern account-info">
            <div class="info-icon">
              <i class="fas fa-credit-card"></i>
            </div>
            <div class="info-content">
              <span class="info-label">Conta</span>
              <span class="info-value">{{ bankData.accountId }}</span>
            </div>
          </div>

          <div class="info-card-modern balance-info">
            <div class="info-icon">
              <i class="fas fa-wallet"></i>
            </div>
            <div class="info-content">
              <span class="info-label">Saldo Disponível</span>
              <span class="info-value" :class="balanceClass">
                {{ formatCurrency(balance.amount) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div v-if="transactions.length" class="filters-section">
        <div class="filters-card">
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-exchange-alt filter-icon"></i>
                Tipo
              </label>
              <select v-model="filterType" class="modern-select">
                <option value="all">Todos</option>
                <option value="CREDIT">Crédito</option>
                <option value="DEBIT">Débito</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-search filter-icon"></i>
                Buscar
              </label>
              <input
                type="text"
                v-model="searchTerm"
                placeholder="Digite a descrição ou valor..."
                class="modern-input"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div v-if="transactions.length" class="summary-section">
        <div class="summary-cards-grid">
          <div class="summary-card-modern credits-card">
            <div class="summary-icon">
              <i class="fas fa-arrow-up"></i>
            </div>
            <div class="summary-content">
              <span class="summary-label">Total de Créditos</span>
              <span class="summary-value">{{
                formatCurrency(totals.credits)
              }}</span>
            </div>
          </div>

          <div class="summary-card-modern debits-card">
            <div class="summary-icon">
              <i class="fas fa-arrow-down"></i>
            </div>
            <div class="summary-content">
              <span class="summary-label">Total de Débitos</span>
              <span class="summary-value">{{
                formatCurrency(totals.debits)
              }}</span>
            </div>
          </div>

          <div class="summary-card-modern reconciled-card">
            <div class="summary-icon">
              <i class="fas fa-check-double"></i>
            </div>
            <div class="summary-content">
              <span class="summary-label">Transações Conciliadas</span>
              <span class="summary-value">
                {{ reconciledCount }} / {{ transactions.length }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Transactions Table -->
      <div v-if="transactions.length" class="transactions-section">
        <div class="table-card">
          <div class="table-header-modern">
            <h3 class="table-title">
              <i class="fas fa-list-ul mr-2"></i>
              Transações Bancárias
            </h3>
            <div class="table-actions">
              <span class="records-count"
                >{{ filteredTransactions.length }} registro(s)</span
              >
            </div>
          </div>

          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th class="actions-column">Ações</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="transaction in filteredTransactions"
                  :key="transaction.fitId"
                  :class="{
                    'row-find-finance':
                      transaction.findFinanceByOFX.items.length > 0 ||
                      transaction.findFinanceByOFX.item,
                    'row-not-find-finance':
                      !transaction.findFinanceByOFX.item &&
                      transaction.findFinanceByOFX.items.length == 0,
                  }"
                >
                  <td class="date-column">
                    {{ formatDate(transaction.date) }}
                  </td>
                  <td>
                    <span
                      :class="[
                        'type-badge',
                        transaction.type === 'CREDIT'
                          ? 'badge-credit'
                          : 'badge-debit',
                      ]"
                    >
                      <i
                        :class="[
                          'fas',
                          transaction.type === 'CREDIT'
                            ? 'fa-plus-circle'
                            : 'fa-minus-circle',
                          'mr-1',
                        ]"
                      ></i>
                      {{ transaction.type === "CREDIT" ? "Crédito" : "Débito" }}
                    </span>
                  </td>
                  <td class="description-column">
                    {{ transaction.memo }}
                  </td>
                  <td>
                    <span
                      :class="[
                        'amount-value',
                        transaction.type === 'CREDIT'
                          ? 'amount-credit'
                          : 'amount-debit',
                      ]"
                    >
                      {{ formatCurrency(transaction.amount) }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="!transaction.findFinanceByOFX.item"
                      :class="['status-badge-modern', 'status-pending']"
                    >
                      <span class="mr-1">&#10008;</span>
                      Pendente
                    </span>
                    <span
                      v-if="
                        transaction.findFinanceByOFX.item &&
                        transaction.findFinanceByOFX.item.bank_reconciliation_id
                      "
                      class="status-badge-modern status-reconciled"
                    >
                      <span class="mr-1">&#10003;</span>
                      Conciliado
                    </span>
                  </td>
                  <td class="actions-column">
                    <div
                      class="action-buttons"
                      v-if="
                        transaction.findFinanceByOFX.item ||
                        transaction.findFinanceByOFX.items.length > 0
                      "
                    >
                      <v-btn
                        class="btn-modern btn-modern-save"
                        v-if="!transaction.findFinanceByOFX.item"
                        :disabled="loading"
                        @click="handleModalReconcileTransaction(transaction)"
                      >
                        <i class="fas fa-check mr-2"></i>
                        Conciliar transação
                      </v-btn>

                      <span
                        v-if="
                          transaction.findFinanceByOFX.item &&
                          transaction.findFinanceByOFX.item
                            .bank_reconciliation_id
                        "
                        class="status-badge-modern status-reconciled"
                      >
                        <span class="mr-1">&#10003;</span>
                        Conciliado
                      </span>
                    </div>
                    <div
                      v-if="
                        !transaction.findFinanceByOFX.item &&
                        transaction.findFinanceByOFX.items.length == 0
                      "
                    >
                      <v-btn
                        class="btn-modern btn-modern-delete"
                        @click="
                          handleModalCreateFinanceReconcileTransaction(
                            transaction
                          )
                        "
                      >
                        <span class="mr-1">&#10008;</span>
                        Lançamento não encontrado
                      </v-btn>
                    </div>
                  </td>

                  <td>
                    <span v-if="!transaction.findFinanceByOFX.item">
                      {{ transaction.findFinanceByOFX.items.length }}
                    </span>
                  </td>

                  <td>
                    <div
                      v-if="
                        !transaction.findFinanceByOFX.item &&
                        transaction.findFinanceByOFX.items.length == 0
                      "
                    >
                      <div
                        v-if="
                          bank_reconciliation_suggestion == 'SIM' &&
                          transaction.type === 'CREDIT'
                        "
                      >
                        {{ transaction.findFinanceByOFX.clients.length }}
                      </div>

                      <div
                        v-if="
                          bank_reconciliation_suggestion == 'SIM' &&
                          transaction.type === 'DEBIT'
                        "
                      >
                        {{ transaction.findFinanceByOFX.providers.length }}
                      </div>
                    </div>
                  </td>

                  <b-modal
                    :id="`modal-create-reconcile-transaction-${transaction.fitId}`"
                    hide-footer
                    no-close-on-backdrop
                    modal-class="modern-modal"
                    scrollable
                    size="lg"
                  >
                    <template v-slot:modal-title>
                      <div class="modal-title-modern">
                        <i class="fas fa-check"></i>
                        Criar lançamento e conciliar transação
                      </div>
                    </template>
                    <div class="modal-content-modern p-2">
                      <div class="content-card">
                        <div class="filters-grid">
                          <div class="filter-group full-width">
                            <label class="filter-label">Descrição</label>
                            <input
                              type="text"
                              :value="transaction.memo"
                              class="modern-input"
                              disabled
                            />
                          </div>
                          <div class="filter-group">
                            <label class="filter-label">Tipo</label>
                            <input
                              type="text"
                              :value="
                                transaction.type == 'CREDIT'
                                  ? 'CRÉDITO'
                                  : 'DÉBITO'
                              "
                              class="modern-input"
                              disabled
                            />
                          </div>
                          <div class="filter-group">
                            <label class="filter-label">Valor</label>
                            <input
                              type="text"
                              :value="formatCurrency(transaction.amount)"
                              class="modern-input"
                              disabled
                            />
                          </div>
                          <div class="filter-group">
                            <label class="filter-label">Vencimento</label>
                            <input
                              type="date"
                              v-model="due_date"
                              class="modern-input"
                            />
                          </div>
                          <div class="filter-group full-width">
                            <label class="filter-label">Categoria</label>
                            <treeselect
                              :options="finance_configuration_category"
                              placeholder="SELECIONE..."
                              v-model="finance_configuration_category_id"
                              noChildrenText="NÃO POSSUI CATEGORIA(S) FILHA(S)"
                            />
                          </div>

                          <div
                            class="filter-group full-width vue-treeselect-required"
                          >
                            <label class="filter-label">
                              {{
                                transaction.type == "CREDIT"
                                  ? "Cliente"
                                  : "Fornecedor"
                              }}
                            </label>

                            <vs-select
                              v-if="transaction.type == 'CREDIT'"
                              autocomplete
                              style="width: 100%"
                              v-model="finance_client_id"
                            >
                              <vs-select-item value="" text="Cliente..." />
                              <vs-select-item
                                v-for="client in finance_client"
                                :key="client.id"
                                :value="client.id"
                                :text="`${
                                  client.resident_id ? '(RESIDENTE) - ' : ''
                                }${client.name}`"
                              />
                            </vs-select>

                            <vs-select
                              v-if="transaction.type == 'DEBIT'"
                              autocomplete
                              style="width: 100%"
                              v-model="provider_id"
                            >
                              <vs-select-item value="" text="Fornecedor..." />
                              <vs-select-item
                                v-for="provider in providers"
                                :key="provider.id"
                                :value="provider.id"
                                :text="provider.company_name"
                              />
                            </vs-select>
                          </div>

                          <div class="filter-group full-width">
                            <ErrorsMessages :errors="errors" />
                          </div>
                        </div>

                        <div class="text-right">
                          <ConfirmationDialog
                            v-if="!transaction.findFinanceByOFX.item"
                            :disabled="loading"
                            headerClass="modal-header-gren"
                            message-title="Conciliar transação?"
                            message-body="Deseja realmente concilar a transação? Não é possível reverter o processo!"
                            open-class="btn-modern btn-modern-save"
                            open-left-icon="fa-check"
                            openText="Conciliar transação cadastrando o lançamento"
                            @yes="
                              createFinanceReconcileTransaction(transaction)
                            "
                          />
                        </div>
                      </div>

                      <div
                        v-if="
                          bank_reconciliation_suggestion == 'SIM' &&
                          transaction.type === 'CREDIT'
                        "
                      >
                        <div
                          v-if="transaction.findFinanceByOFX.clients.length > 0"
                          class="content-card"
                        >
                          <div class="alert alert-warning">
                            Lançamentos com valores originais diferentes do
                            arquivo OFX (Podendo ter juros/multa/desconto).
                          </div>

                          <div class="alert alert-info">
                            Está sendo considerado lançamentos com a diferença
                            máxima permitida de
                            <strong>{{
                              bank_reconciliation_suggestion_percentage
                            }}</strong
                            >%.
                          </div>

                          <div
                            class="content-card"
                            v-for="(
                              findFinanceByOFXClient, KeyFindFinanceByOFXClient
                            ) in transaction.findFinanceByOFX.clients"
                            :key="`provider-${transaction.fitId}-${KeyFindFinanceByOFXClient}`"
                          >
                            <div>
                              <p class="m-0">
                                Nome:
                                <strong>{{
                                  findFinanceByOFXClient.name
                                }}</strong>
                              </p>
                              <p class="m-0">
                                Documento:
                                <strong>
                                  {{
                                    findFinanceByOFXClient.cpf_cnpj
                                      ? findFinanceByOFXClient.cpf_cnpj
                                      : "--"
                                  }}
                                </strong>
                              </p>
                            </div>
                            <div class="table-responsive">
                              <table class="table table-sm">
                                <thead>
                                  <th>Descrição</th>
                                  <th>Valor</th>
                                  <th>Vencimento</th>
                                  <th></th>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="(
                                      findFinanceByOFXClientItem,
                                      KeyFindFinanceByOFXClientItem
                                    ) in findFinanceByOFXClient.items"
                                    :key="`${transaction.fitId}-${KeyFindFinanceByOFXClient}-${KeyFindFinanceByOFXClientItem}`"
                                  >
                                    <td>
                                      {{
                                        findFinanceByOFXClientItem.description
                                      }}
                                    </td>
                                    <td>
                                      R$ -
                                      {{
                                        formatPrice(
                                          String(
                                            findFinanceByOFXClientItem.value
                                          )
                                        )
                                      }}
                                    </td>
                                    <td>
                                      {{
                                        moment(
                                          findFinanceByOFXClientItem.due_date
                                        ).format("DD/MM/YYYY")
                                      }}
                                    </td>
                                    <td>
                                      <ConfirmationDialog
                                        v-if="
                                          !transaction.findFinanceByOFX.item
                                        "
                                        :disabled="loading"
                                        headerClass="modal-header-gren"
                                        message-title="Conciliar transação?"
                                        message-body="Deseja realmente concilar a transação? Não é possível reverter o processo!"
                                        open-class="btn-modern btn-modern-save"
                                        open-left-icon="fa-check"
                                        openText="Conciliar transação"
                                        @yes="
                                          reconcileTransaction(
                                            transaction,
                                            findFinanceByOFXClientItem
                                          )
                                        "
                                      />
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="
                          bank_reconciliation_suggestion == 'SIM' &&
                          transaction.type === 'DEBIT'
                        "
                      >
                        <div
                          v-if="
                            transaction.findFinanceByOFX.providers.length > 0
                          "
                          class="content-card"
                        >
                          <div class="alert alert-warning">
                            Lançamentos com valores originais diferentes do
                            arquivo OFX (Podendo ter juros/multa/desconto).
                          </div>

                          <div class="alert alert-info">
                            Está sendo considerado lançamentos com a diferença
                            máxima permitida de
                            <strong>{{
                              bank_reconciliation_suggestion_percentage
                            }}</strong
                            >%.
                          </div>

                          <div
                            class="content-card"
                            v-for="(
                              findFinanceByOFXProvider,
                              KeyFindFinanceByOFXProvider
                            ) in transaction.findFinanceByOFX.providers"
                            :key="`provider-${transaction.fitId}-${KeyFindFinanceByOFXProvider}`"
                          >
                            <div>
                              <p class="m-0">
                                Nome:
                                <strong>{{
                                  findFinanceByOFXProvider.company_name
                                }}</strong>
                              </p>
                              <p class="m-0">
                                Documento:
                                <strong>
                                  {{
                                    findFinanceByOFXProvider.cpf_cnpj
                                      ? findFinanceByOFXProvider.cpf_cnpj
                                      : "--"
                                  }}
                                </strong>
                              </p>
                            </div>
                            <div class="table-responsive">
                              <table class="table table-sm">
                                <thead>
                                  <th>Descrição</th>
                                  <th>Valor</th>
                                  <th>Vencimento</th>
                                  <th></th>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="(
                                      findFinanceByOFXProviderItem,
                                      KeyFindFinanceByOFXProviderItem
                                    ) in findFinanceByOFXProvider.items"
                                    :key="`${transaction.fitId}-${KeyFindFinanceByOFXProvider}-${KeyFindFinanceByOFXProviderItem}`"
                                  >
                                    <td>
                                      {{
                                        findFinanceByOFXProviderItem.description
                                      }}
                                    </td>
                                    <td>
                                      R$ -
                                      {{
                                        formatPrice(
                                          String(
                                            findFinanceByOFXProviderItem.value
                                          )
                                        )
                                      }}
                                    </td>
                                    <td>
                                      {{
                                        moment(
                                          findFinanceByOFXProviderItem.due_date
                                        ).format("DD/MM/YYYY")
                                      }}
                                    </td>
                                    <td>
                                      <ConfirmationDialog
                                        v-if="
                                          !transaction.findFinanceByOFX.item
                                        "
                                        :disabled="loading"
                                        headerClass="modal-header-gren"
                                        message-title="Conciliar transação?"
                                        message-body="Deseja realmente concilar a transação? Não é possível reverter o processo!"
                                        open-class="btn-modern btn-modern-save"
                                        open-left-icon="fa-check"
                                        openText="Conciliar transação"
                                        @yes="
                                          reconcileTransaction(
                                            transaction,
                                            findFinanceByOFXProviderItem
                                          )
                                        "
                                      />
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-modal>

                  <b-modal
                    :id="`modal-reconcile-transaction-${transaction.fitId}`"
                    hide-footer
                    no-close-on-backdrop
                    modal-class="modern-modal"
                    scrollable
                    size="xl"
                  >
                    <template v-slot:modal-title>
                      <div class="modal-title-modern">
                        <i class="fas fa-check"></i>
                        Conciliar transação
                      </div>
                    </template>
                    <div class="modal-content-modern">
                      <div
                        class="text-center p-5 border"
                        v-if="transaction.findFinanceByOFX.items.length == 0"
                      >
                        <p>0 informações encontradas!</p>
                      </div>

                      <div
                        v-if="transaction.findFinanceByOFX.items.length > 0"
                        class="table-responsive"
                      >
                        <table class="table table-sm">
                          <thead>
                            <th>
                              {{
                                transaction.type == "CREDIT"
                                  ? "Cliente"
                                  : "Fornecedor"
                              }}
                            </th>
                            <th>Documento</th>
                            <th>Descrição</th>
                            <th>Valor</th>
                            <th>Vencimento</th>
                            <th></th>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(
                                findFinanceByOFXItem, keyFindFinanceByOFXItem
                              ) in transaction.findFinanceByOFX.items"
                              :key="`${transaction.fitId}-${keyFindFinanceByOFXItem}`"
                            >
                              <td>
                                <span
                                  v-if="
                                    findFinanceByOFXItem.spent_or_gain ==
                                    'CREDITO'
                                  "
                                  >{{
                                    findFinanceByOFXItem.finance_client_name
                                      ? findFinanceByOFXItem.finance_client_name
                                      : "--"
                                  }}</span
                                >
                                <span
                                  v-if="
                                    findFinanceByOFXItem.spent_or_gain ==
                                    'DEBITO'
                                  "
                                  >{{
                                    findFinanceByOFXItem.provider_company_name
                                      ? findFinanceByOFXItem.provider_company_name
                                      : "--"
                                  }}</span
                                >
                              </td>
                              <td>
                                <span
                                  v-if="
                                    findFinanceByOFXItem.spent_or_gain ==
                                    'CREDITO'
                                  "
                                  >{{
                                    findFinanceByOFXItem.finance_client_cpf_cnpj
                                      ? findFinanceByOFXItem.finance_client_cpf_cnpj
                                      : "--"
                                  }}</span
                                >
                                <span
                                  v-if="
                                    findFinanceByOFXItem.spent_or_gain ==
                                    'DEBITO'
                                  "
                                  >{{
                                    findFinanceByOFXItem.provider_cpf_cnpj
                                      ? findFinanceByOFXItem.provider_cpf_cnpj
                                      : "--"
                                  }}</span
                                >
                              </td>
                              <td>
                                {{ findFinanceByOFXItem.description }}
                              </td>
                              <td>
                                R$
                                <span
                                  v-if="
                                    findFinanceByOFXItem.spent_or_gain ==
                                    'DEBITO'
                                  "
                                  >-</span
                                >
                                {{
                                  formatPrice(
                                    String(findFinanceByOFXItem.value)
                                  )
                                }}
                              </td>
                              <td>
                                {{
                                  moment(findFinanceByOFXItem.due_date).format(
                                    "DD/MM/YYYY"
                                  )
                                }}
                              </td>
                              <td>
                                <ConfirmationDialog
                                  v-if="!transaction.findFinanceByOFX.item"
                                  :disabled="loading"
                                  headerClass="modal-header-gren"
                                  message-title="Conciliar transação?"
                                  message-body="Deseja realmente concilar a transação? Não é possível reverter o processo!"
                                  open-class="btn-modern btn-modern-save"
                                  open-left-icon="fa-check"
                                  openText="Conciliar transação"
                                  @yes="
                                    reconcileTransaction(
                                      transaction,
                                      findFinanceByOFXItem
                                    )
                                  "
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </b-modal>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!transactions.length" class="empty-state-modern">
        <div class="empty-icon">
          <i class="fas fa-file-invoice fa-3x"></i>
        </div>
        <h3 class="empty-title">Nenhuma transação importada</h3>
        <p class="empty-text">
          Importe um arquivo OFX para começar a conciliação bancária
        </p>
        <v-btn class="btn-import-empty" @click="$refs.fileInput.click()">
          <i class="fas fa-file-upload mr-2"></i>
          Importar Arquivo OFX
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages.vue";

import ConfirmationDialog from "../Components/ConfirmationDialog.vue";

import Treeselect from "@riophae/vue-treeselect";

import api from "../../services/api";

import toast from "../../lib/ToastNotification";

import { formatPrice } from "../../lib/format";

import { getRestHomeSelectedFinancialAccount } from "../../lib/functions";

import moment from "moment";

export default {
  name: "BankReconciliation",

  components: {
    ErrorsMessages,
    ConfirmationDialog,
    Treeselect,
  },

  data() {
    return {
      bank_reconciliation_suggestion: "NAO",
      bank_reconciliation_suggestion_percentage: 0,

      bankData: null,
      balance: null,
      transactions: [],
      filterType: "all",
      searchTerm: "",

      due_date: "",
      finance_client_id: "",
      provider_id: "",
      finance_configuration_category_id: "",

      providers: [],
      finance_client: [],
      finance_configuration_category: [],

      loading: false,
      errors: [],
    };
  },

  computed: {
    filteredTransactions() {
      return this.transactions.filter((t) => {
        // Filtro de tipo
        if (this.filterType !== "all" && t.type !== this.filterType)
          return false;

        // Busca por texto
        if (this.searchTerm) {
          const search = this.searchTerm.toLowerCase();
          const matchMemo = t.memo.toLowerCase().includes(search);
          const matchAmount = t.amount.toString().includes(search);
          if (!matchMemo && !matchAmount) return false;
        }

        return true;
      });
    },

    totals() {
      return this.transactions.reduce(
        (acc, t) => {
          if (t.type === "CREDIT") {
            acc.credits += t.amount;
          } else {
            acc.debits += Math.abs(t.amount);
          }
          return acc;
        },
        { credits: 0, debits: 0 }
      );
    },

    reconciledCount() {
      return this.transactions.filter((t) => t.findFinanceByOFX.item).length;
    },

    balanceClass() {
      if (!this.bankData) return "";
      return this.balance.amount >= 0 ? "positive" : "negative";
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    getRestHomeSelectedFinancialAccount,
    formatPrice,
    moment,

    async retrieve() {
      await this.getFinanceClients();
      await this.getProviders();
      await this.getFinanceConfigurationCategorys();
    },

    handleModalCreateFinanceReconcileTransaction(transaction) {
      this.due_date = "";
      this.finance_client_id = "";
      this.provider_id = "";
      this.finance_configuration_category_id = "";
      this.$bvModal.show(
        `modal-create-reconcile-transaction-${transaction.fitId}`
      );
    },

    handleModalReconcileTransaction(transaction) {
      this.$bvModal.show(`modal-reconcile-transaction-${transaction.fitId}`);
    },

    async handleFileUpload(event) {
      this.errors = [];

      const file = event.target.files[0];

      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        // Chama API backend para processar OFX
        const response = await api.post("/api/parse-ofx", formData);

        const {
          error,
          errors,
          bankData,
          balance,
          transactions,
          bank_reconciliation_suggestion,
          bank_reconciliation_suggestion_percentage,
        } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.bankData = bankData;
          this.balance = balance;
          this.transactions = transactions;
          this.bank_reconciliation_suggestion = bank_reconciliation_suggestion;
          this.bank_reconciliation_suggestion_percentage =
            bank_reconciliation_suggestion_percentage;

          toast(
            `${transactions.length} transações importadas com sucesso`,
            "success"
          );
        }
      } catch (error) {
        this.errors = ["Erro ao importar arquivo OFX"];
        toast("Erro ao importar arquivo OFX", "error");
      }
    },

    async reconcileTransaction(transaction, item) {
      await this.updateTransaction(transaction, item);
    },

    async updateTransaction(transaction, item) {
      try {
        this.loading = true;

        let body = {
          finance_configuration_account_id:
            transaction.finance_configuration_account_id,
          date: transaction.date,
          amount: transaction.amount,
          type: transaction.type,
          memo: transaction.memo,
          item,
        };

        const response = await api.put(
          `/api/transactions/${transaction.fitId}`,
          body
        );

        const { error, message } = response.data;

        if (!error) {
          this.$bvModal.hide(
            `modal-reconcile-transaction-${transaction.fitId}`
          );
          this.$bvModal.hide(
            `modal-create-reconcile-transaction-${transaction.fitId}`
          );

          toast(message, "success");
        } else {
          this.errors.push(message);
        }

        const fileInput = this.$refs.fileInput;
        if (fileInput && fileInput.files && fileInput.files[0]) {
          const file = fileInput.files[0];
          await this.handleFileUpload({ target: { files: [file] } });
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },

    async createFinanceReconcileTransaction(transaction) {
      try {
        this.loading = true;

        let {
          due_date,
          finance_client_id,
          provider_id,
          finance_configuration_category_id,
        } = this;

        let body = {
          finance_configuration_account_id:
            transaction.finance_configuration_account_id,
          date: transaction.date,
          amount: transaction.amount,
          type: transaction.type,
          memo: transaction.memo,
          due_date,
          finance_client_id,
          provider_id,
          finance_configuration_category_id,
        };

        const response = await api.post(
          `/api/transactions/${transaction.fitId}`,
          body
        );

        const { error, errors, message } = response.data;

        if (!error) {
          this.$bvModal.hide(
            `modal-create-reconcile-transaction-${transaction.fitId}`
          );
          this.$bvModal.hide(
            `modal-create-reconcile-transaction-${transaction.fitId}`
          );

          toast(message, "success");

          const fileInput = this.$refs.fileInput;
          if (fileInput && fileInput.files && fileInput.files[0]) {
            const file = fileInput.files[0];
            await this.handleFileUpload({ target: { files: [file] } });
          }
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    },

    async getFinanceClients() {
      try {
        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        let params = {
          rest_home_id_selected_financial_account,
        };

        const response = await api.get("/rest-home/financial/client-actives", {
          params,
        });

        const { data } = response.data;

        this.finance_client = data;
      } catch (error) {
        console.log(error);
      }
    },

    async getProviders() {
      try {
        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        const params = { rest_home_id_selected_financial_account, active: 1 };

        const response = await api.get("/rest-home/providers", {
          params,
        });

        const { providers } = response.data;

        this.providers = providers;
      } catch (error) {
        console.log(error);
      }
    },

    async getFinanceConfigurationCategorys() {
      try {
        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        let params = {
          rest_home_id_selected_financial_account,
        };

        const response = await api.get(
          "/rest-home/financial/configuration/category-actives",
          {
            params,
          }
        );

        const { data } = response.data;

        let finance_configuration_category = [];
        for (let category of data) {
          finance_configuration_category.push(category);
          if (category.default == 1) {
            this.finance_configuration_category_id = category.id;
          }
        }

        this.finance_configuration_category = finance_configuration_category;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.bank-reconciliation-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

/* Header Section */
.reconciliation-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.btn-upload-modern {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-upload-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4) !important;
}

/* Container */
.reconciliation-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 32px 24px;
}

/* Account Information */
.account-section {
  margin-bottom: 32px;
}

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.info-card-modern {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.info-card-modern:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.info-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.bank-info .info-icon {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
}

.account-info .info-icon {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.balance-info .info-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.info-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.info-value.positive {
  color: #10b981;
}

.info-value.negative {
  color: #ef4444;
}

/* Filters Section */
.filters-section {
  margin-bottom: 24px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group.full-width {
  grid-column: 1 / -1;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-select,
.modern-input {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-select:focus,
.modern-input:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Summary Section */
.summary-section {
  margin-bottom: 24px;
}

.summary-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.summary-card-modern {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.summary-card-modern:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.summary-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.credits-card .summary-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.debits-card .summary-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.reconciled-card .summary-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.summary-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

/* Transactions Table */
.transactions-section {
  margin-bottom: 24px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.table-header-modern {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.records-count {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead {
  background: #f8fafc;
}

.modern-table thead th {
  padding: 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
}

.modern-table tbody td {
  padding: 16px;
  border-bottom: 1px solid #f1f3f5;
  font-size: 14px;
  color: #1e293b;
}

.modern-table tbody tr {
  transition: all 0.2s ease;
}

.modern-table tbody tr:hover {
  background: #f8fafc;
}

.modern-table tbody tr.row-find-finance {
  background: #e6fff2 !important;
}

.modern-table tbody tr.row-not-find-finance {
  background: #ffe6e6 !important;
}

.checkbox-column {
  width: 40px;
  text-align: center;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #0f65b0;
}

.date-column {
  white-space: nowrap;
  color: #64748b;
  font-weight: 500;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-credit {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.badge-debit {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.description-column {
  max-width: 300px;
}

.amount-value {
  font-weight: 700;
  font-size: 15px;
}

.amount-credit {
  color: #10b981;
}

.amount-debit {
  color: #ef4444;
}

.status-badge-modern {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-reconciled {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  border: 1px solid #f59e0b;
}

.actions-column {
  width: 120px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-undo-modern {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
}

.btn-edit-modern {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: white !important;
  border: none !important;
}

/* Bulk Actions */
.bulk-actions-section {
  margin-bottom: 24px;
}

.bulk-actions-card {
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.bulk-info {
  display: flex;
  align-items: center;
  color: #1e293b;
  font-size: 15px;
}

.bulk-buttons {
  display: flex;
  gap: 12px;
}

.btn-bulk-reconcile-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 10px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-bulk-reconcile-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

.btn-bulk-clear-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 10px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.btn-bulk-clear-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
}

/* Empty State */
.empty-state-modern {
  text-align: center;
  padding: 80px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.empty-icon {
  width: 120px;
  height: 120px;
  margin: 0 auto 24px auto;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  color: #94a3b8;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.empty-text {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 32px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.btn-import-empty {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-import-empty:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4) !important;
}

.btn-close-modern {
  background: transparent !important;
  border: none !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  color: #64748b !important;
  font-size: 20px !important;
}

.btn-close-modern:hover {
  background: #f8fafc !important;
  color: #1e293b !important;
}

.form-group-modern {
  margin-bottom: 20px;
}

.form-group-modern:last-child {
  margin-bottom: 0;
}

.form-label-modern {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.form-select-modern,
.form-textarea-modern {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  font-family: inherit;
}

.form-select-modern:focus,
.form-textarea-modern:focus {
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
  outline: none;
}

.form-textarea-modern {
  resize: vertical;
  min-height: 100px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .info-cards-grid {
    grid-template-columns: 1fr;
  }

  .summary-cards-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .reconciliation-container {
    padding: 0 16px 24px 16px;
  }

  .reconciliation-header {
    padding: 20px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .bulk-actions-card {
    flex-direction: column;
    align-items: stretch;
  }

  .bulk-buttons {
    flex-direction: column;
  }

  .table-wrapper {
    overflow-x: scroll;
  }

  .modern-table {
    min-width: 900px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .btn-upload-modern {
    width: 100%;
    justify-content: center;
  }

  .info-card-modern {
    padding: 20px;
  }

  .info-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .summary-card-modern {
    padding: 20px;
  }

  .summary-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
}

/* Custom Scrollbar */
.table-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Content Card */
.content-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 24px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 5px;
}
</style>